import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Avatar, Box, FieldWrapper, FlexBox, InputSelectionList, SelectionList, Spinner, Text, } from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import { useCallback, useEffect, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import { searchApi } from 'apis/search';
import { useLazyPaginatedEndpoint } from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { getUserDisplayName } from 'utils/user';
import styles from './contactSearchInput.module.scss';
/**
 * Contact Search Input is an input field that are used to search for a contact
 * to be merged with another contact.
 */
export const ContactSearchInput = ({ disableContactsWithProps, onChange, }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState();
    const { changeArg, items: searchUsers, asyncStatus, hasNextPage, nextPage, } = useLazyPaginatedEndpoint({
        endpoint: searchApi.endpoints.readSearchUser,
        itemsSelector: ({ data }) => searchQuery.length > 0 ? data.results.hits : [],
        infinite: true,
    });
    useEffect(() => {
        changeArg({
            searchTerm: searchQuery,
            filter: {
                facets: {
                    roles: ['enduser'],
                },
            },
            offset: 0,
        }, {
            debounceTime: 200,
        });
    }, [changeArg, searchQuery]);
    const isContactDisabled = (searchUser) => {
        var _a;
        return ((_a = disableContactsWithProps === null || disableContactsWithProps === void 0 ? void 0 : disableContactsWithProps.some((prop) => prop && !!searchUser[prop])) !== null && _a !== void 0 ? _a : false);
    };
    // Override the automatic positioning calculated by the Dropdown component, which doesn't always
    // work as expected because the dropdown may be rendered inside a transformed container.
    // These styles makes sure the dropdown is rendered below the search input field.
    const dropdownPositionOverride = {
        top: '100%',
        bottom: 'auto',
        margin: `${sizes.spacer[200]} 0 0 0`,
    };
    const handleOnChange = useCallback((contact) => {
        setSelectedUser(contact);
        onChange === null || onChange === void 0 ? void 0 : onChange(contact);
    }, [onChange]);
    const handleToggleDropdown = useCallback((isOpen) => {
        if (isOpen)
            return;
        if (!selectedUser) {
            setSearchQuery('');
        }
    }, [selectedUser]);
    return (_jsx(Box, { children: _jsx(FieldWrapper, Object.assign({ label: "Select a contact" }, { children: _jsxs(InputSelectionList, Object.assign({ value: selectedUser ? [selectedUser] : undefined, onChange: (e) => handleOnChange(e.selectedValues[0]), onToggleDropdown: handleToggleDropdown, getItemTitleByValue: getUserDisplayName, filter: {
                    name: 'mergeContactSearchInput',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }, dropdownProps: {
                    className: styles.inputSelectionDropdown,
                    style: Object.assign(Object.assign({}, dropdownPositionOverride), { display: searchQuery.length === 0 ||
                            (searchUsers.length === 0 && asyncStatus !== 'pending')
                            ? 'none'
                            : undefined }),
                }, placeholder: 'Search after name, phone number or email' }, { children: [searchUsers.map((searchUser) => {
                        var _a;
                        const name = getUserDisplayName(searchUser);
                        const secondLine = (_a = searchUser.email) !== null && _a !== void 0 ? _a : searchUser.phone_number;
                        const isNameAndSecondLineEqual = name === secondLine;
                        return (_jsx(SelectionList.Item, Object.assign({ value: searchUser, isDisabled: isContactDisabled(searchUser) }, { children: _jsxs(FlexBox, Object.assign({ gap: "200", alignItems: "center" }, { children: [_jsx(Box, { children: _jsx(Avatar, { user: {
                                                name: searchUser.display_name,
                                                avatar_url: searchUser.avatar_url,
                                                phone_number: searchUser.phone_number,
                                                email: searchUser.email,
                                            } }) }), _jsxs(FlexBox, Object.assign({ flexDirection: "column" }, { children: [_jsx(Box, { children: _jsx(Text.Heading, Object.assign({ variant: "heading-4" }, { children: name })) }), !isNameAndSecondLineEqual && (_jsxs(FlexBox, Object.assign({ gap: "200", alignItems: "center" }, { children: [searchUser.phone_number && (_jsx(Text.Paragraph, Object.assign({ variant: "4", subdued: true }, { children: searchUser.phone_number }))), searchUser.email && (_jsx(Text.Paragraph, Object.assign({ variant: "4", subdued: true }, { children: searchUser.email })))] })))] }))] })) }), searchUser.id));
                    }), searchQuery.length && asyncStatus === 'pending' && (_jsx(FlexBox, Object.assign({ alignItems: "center", justifyContent: "center", padding: "400 200" }, { children: _jsx(Spinner, { size: 24 }) }))), searchQuery.length && asyncStatus === 'fulfilled' && hasNextPage && (_jsx(Waypoint, { onEnter: nextPage }))] })) })) }));
};
